// KeyValue (job page round): labelled facts on hairlines (a job's Details, a form's answers, what's at an
// address, a hover card's fields). grid: the label muted on the left, its value beside it; spread: the
// thing in ink on the left, its value muted on the right ("Furnace · 2016"). A fact that is a problem has
// StateMark's red bar before its value, the words in ink (DESIGN.md §2: red is a mark, never text). The board's hover card and side panel take it in place of their
// stand-in, with the same props. Truck stock (BL-203): figures, a page's or a place's facts side by side
// (Below minimum 5 · No minimum 2 · To count 1 · Last restock Sat 24 Oct), each its label, the figure
// large and a muted line under it; at most five, ruled above and below, hairlines between, two to a row
// on a phone. A figure that's a problem has the red bar before it, its words saying why. Reads only
// --kv-*. Not the primitives' Figures: that is a legacy one-off in base.css (raw sizes, no problem state,
// counted as a one-off by the coverage check); its callers move onto this layout instead (BL follow-up).
// Rows (BL-189, Sales tax's returns): figures one under another, the label muted on the left, the
// amount on the right in tabular figures, bold; no hairlines. A row's tone makes the total strong (its
// label in ink, bold) or a by-the-way figure muted ("Rounding on the lines").
import type { ReactNode } from 'react';
import { StateMark } from '../StateMark/StateMark.tsx';
import './KeyValue.css';

export interface KeyValueItem {
  label: ReactNode;
  value: ReactNode;
  /** A problem: StateMark's red bar before the value (figures: the figure); the words stay ink. */
  bad?: boolean;
  /** Figures: the muted line under the figure ("the refill brings these"). */
  sub?: ReactNode;
  /** Rows: strong, the total (its label in ink, bold); muted, a by-the-way figure (its value muted). */
  tone?: 'strong' | 'muted';
}

export interface KeyValueProps {
  items: KeyValueItem[];
  /** Tight rows, for a hover card. */
  density?: 'normal' | 'tight';
  /**
   * grid: label beside value; spread: a thing and its value; figures: facts side by side, at most five;
   * rows: amounts one under another, the label left and the amount right-aligned.
   */
  layout?: 'grid' | 'spread' | 'figures' | 'rows';
  /** Its name for screen readers, when nothing above it names it. */
  label?: string;
  /** Grid: at 600 px or narrower each label sits above its value (a read-only sheet's facts). */
  isStackedOnPhone?: boolean;
}

export function KeyValue({
  items,
  density = 'normal',
  layout = 'grid',
  label,
  isStackedOnPhone,
}: KeyValueProps) {
  return (
    <dl
      className="fl-kv"
      data-density={density}
      data-layout={layout}
      data-stacked-on-phone={isStackedOnPhone ? '' : undefined}
      aria-label={label}
    >
      {(layout === 'figures' ? items.slice(0, 5) : items).map((it, i) => (
        <div key={i} className="fl-kv__row" data-bad={it.bad ? '' : undefined} data-tone={it.tone}>
          <dt className="fl-kv__label">{it.label}</dt>
          <dd className="fl-kv__value">
            {layout === 'figures' ? (
              <span className="fl-kv__fig">
                {it.bad ? <StateMark state="problem" /> : null}
                {it.value}
              </span>
            ) : (
              <>
                {it.bad ? <StateMark state="problem" /> : null}
                {it.value}
              </>
            )}
            {it.sub != null ? <span className="fl-kv__sub">{it.sub}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
